/**
 * 素材管理共用工具（表格 / 图片模式）
 */

export function isVideoFile(url) {
  if (!url) return false
  const videoExtensions = ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv', '.m4v']
  return videoExtensions.some(function (ext) {
    return url.toLowerCase().includes(ext)
  })
}

export function getVideoSnapshot(videoUrl, size) {
  const s = size || 80
  return videoUrl + '?x-oss-process=video/snapshot,t_1000,f_jpg,w_' + s + ',h_' + s
}

export function getMaterialTypeBadge(row) {
  const label = getMaterialTypeLabel(row)
  if (label === '模板') {
    return { label: '模板', type: 'warning' }
  }
  return { label: '贴图', type: 'primary' }
}

export function getMaterialTypeLabel(row) {
  const tag = (row && row.tag) ? String(row.tag).trim() : ''
  const tagPrefixMatch = tag.match(/^图片-(贴图|模板)/)
  if (tagPrefixMatch) {
    return tagPrefixMatch[1]
  }
  if (tag.indexOf('模板') !== -1) {
    return '模板'
  }
  if (tag.indexOf('贴图') !== -1) {
    return '贴图'
  }

  const categoryNames = [
    row.firstCategory && row.firstCategory.categoryName,
    row.secondCategory && row.secondCategory.categoryName,
    row.thirdCategory && row.thirdCategory.categoryName
  ].filter(Boolean)

  for (let i = 0; i < categoryNames.length; i++) {
    if (categoryNames[i].indexOf('模板') !== -1) {
      return '模板'
    }
  }
  for (let j = 0; j < categoryNames.length; j++) {
    if (categoryNames[j].indexOf('贴图') !== -1) {
      return '贴图'
    }
  }

  return '贴图'
}

export function formatShortUploadDate(timeValue) {
  if (!timeValue) return '-'
  if (timeValue instanceof Date) {
    return formatCompactDate(timeValue)
  }
  const str = String(timeValue)
  const match = str.match(/(\d{4})-(\d{1,2})-(\d{1,2})/)
  if (match) {
    return parseInt(match[2], 10) + '-' + parseInt(match[3], 10)
  }
  if (str.length >= 10) {
    const part = str.substring(5, 10)
    const pieces = part.split('-')
    if (pieces.length === 2) {
      return parseInt(pieces[0], 10) + '-' + parseInt(pieces[1], 10)
    }
    return part
  }
  return str
}

function formatCompactDate(date) {
  return (date.getMonth() + 1) + '-' + date.getDate()
}

export function formatFullUploadDate(timeValue) {
  if (!timeValue) return '-'
  if (timeValue instanceof Date) {
    return timeValue.toLocaleString()
  }
  return String(timeValue)
}

const PREVIEW_MIN_SCALE = 0.25
const PREVIEW_MAX_SCALE = 6
const PREVIEW_ZOOM_STEP = 0.12
const PREVIEW_DEFAULT_SCALE = 1
const PREVIEW_STYLE_ID = 'ptu-image-preview-style'

let activePreviewClose = null

function ensureImagePreviewStyles() {
  const css = [
    '.ptu-image-preview-overlay {',
    '  position: fixed; inset: 0; z-index: 3000;',
    '  display: flex; align-items: center; justify-content: center;',
    '  background: rgba(0, 0, 0, 0.55);',
    '  cursor: zoom-out;',
    '}',
    '.ptu-image-preview-panel {',
    '  display: flex; flex-direction: column;',
    '  width: 75vw; max-width: none; height: 92vh;',
    '  background: #fff; border-radius: 4px;',
    '  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.28);',
    '  overflow: hidden;',
    '  cursor: default;',
    '}',
    '.ptu-image-preview-header {',
    '  display: flex; align-items: center; gap: 8px;',
    '  padding: 6px 12px; border-bottom: 1px solid #ebeef5;',
    '  flex-shrink: 0;',
    '}',
    '.ptu-image-preview-address {',
    '  flex: 1; min-width: 0;',
    '  display: flex; align-items: center; gap: 4px;',
    '  height: 32px; padding: 0 4px 0 10px;',
    '  border: 1px solid #dcdfe6; border-radius: 16px;',
    '  background: #f5f7fa;',
    '  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;',
    '}',
    '.ptu-image-preview-address:focus-within {',
    '  border-color: #409eff; background: #fff;',
    '  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.15);',
    '}',
    '.ptu-image-preview-address-input {',
    '  flex: 1; min-width: 0; border: none; outline: none;',
    '  background: transparent; font-size: 13px; line-height: 1.4;',
    '  color: #303133; font-family: Consolas, Monaco, "Courier New", monospace;',
    '}',
    '.ptu-image-preview-address-btn {',
    '  flex-shrink: 0; border: none; background: transparent;',
    '  cursor: pointer; color: #909399; padding: 4px 8px;',
    '  font-size: 12px; line-height: 1; border-radius: 12px;',
    '}',
    '.ptu-image-preview-address-btn:hover { color: #409eff; background: rgba(64, 158, 255, 0.08); }',
    '.ptu-image-preview-close {',
    '  flex-shrink: 0; border: none; background: transparent; cursor: pointer;',
    '  font-size: 20px; line-height: 1; color: #909399; padding: 0 4px;',
    '}',
    '.ptu-image-preview-close:hover { color: #409eff; }',
    '.ptu-image-preview-viewport {',
    '  flex: 1; min-height: 0;',
    '  display: flex; align-items: center; justify-content: center;',
    '  overflow: hidden; cursor: zoom-out;',
    '  padding: 6px 12px; box-sizing: border-box;',
    '  background: #686868;',
    '}',
    '.ptu-image-preview-frame {',
    '  position: relative; flex-shrink: 0;',
    '  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.18);',
    '  transform-origin: center center;',
    '  transition: transform 0.05s ease-out;',
    '}',
    '.ptu-image-preview-frame.bg-checker {',
    '  background-color: #b0b0b0;',
    '  background-image: url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'16\' height=\'16\'%3E%3Cpath fill=\'%238a8a8a\' d=\'M0 0h8v8H0zm8 8h8v8H8z\'/%3E%3C/svg%3E");',
    '  background-size: 16px 16px;',
    '  background-repeat: repeat;',
    '}',
    '.ptu-image-preview-frame.bg-white { background: #ffffff; box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.25); }',
    '.ptu-image-preview-frame.bg-dark { background: #3a3a3a; }',
    '.ptu-image-preview-viewport.is-dragging { cursor: grabbing; }',
    '.ptu-image-preview-frame.is-dragging { transition: none; }',
    '.ptu-image-preview-img {',
    '  display: block; width: 100%; height: 100%; cursor: zoom-in;',
    '  user-select: none; -webkit-user-drag: none;',
    '}',
    '.ptu-image-preview-footer {',
    '  padding: 4px 16px; border-top: 1px solid #ebeef5;',
    '  text-align: center; font-size: 12px; color: #909399;',
    '  flex-shrink: 0;',
    '}',
    '.ptu-image-preview-bg-switch {',
    '  display: inline-flex; align-items: center; gap: 6px; margin-right: 12px;',
    '}',
    '.ptu-image-preview-bg-btn {',
    '  border: 1px solid #dcdfe6; background: #fff; color: #606266;',
    '  font-size: 12px; line-height: 1; padding: 4px 8px; border-radius: 3px; cursor: pointer;',
    '}',
    '.ptu-image-preview-bg-btn.is-active {',
    '  border-color: #409eff; color: #409eff; background: #ecf5ff;',
    '}'
  ].join('\n')

  let style = document.getElementById(PREVIEW_STYLE_ID)
  if (style) {
    style.textContent = css
    return
  }
  style = document.createElement('style')
  style.id = PREVIEW_STYLE_ID
  style.textContent = css
  document.head.appendChild(style)
}

/**
 * 打开可缩放图片预览（顶部地址栏可复制/修改、滚轮缩放、按住拖动、ESC 关闭）
 * @param {string} url 图片地址
 * @param {string} [title] 标题（用于 alt / title）
 */
export function openImagePreview(url, title) {
  if (!url) {
    return
  }
  if (activePreviewClose) {
    activePreviewClose()
  }

  ensureImagePreviewStyles()

  const previousOverflow = document.body.style.overflow
  let scale = PREVIEW_DEFAULT_SCALE
  let translateX = 0
  let translateY = 0
  let isDragging = false
  let dragStartX = 0
  let dragStartY = 0
  let dragOriginX = 0
  let dragOriginY = 0
  let closed = false

  const overlay = document.createElement('div')
  overlay.className = 'ptu-image-preview-overlay'
  overlay.setAttribute('tabindex', '-1')

  const panel = document.createElement('div')
  panel.className = 'ptu-image-preview-panel'
  panel.addEventListener('click', function (event) {
    event.stopPropagation()
  })

  const header = document.createElement('div')
  header.className = 'ptu-image-preview-header'

  const addressBar = document.createElement('div')
  addressBar.className = 'ptu-image-preview-address'

  const addressInput = document.createElement('input')
  addressInput.type = 'text'
  addressInput.className = 'ptu-image-preview-address-input'
  addressInput.value = url
  addressInput.setAttribute('aria-label', '图片地址')
  addressInput.spellcheck = false
  addressInput.title = title || '图片地址'

  const copyBtn = document.createElement('button')
  copyBtn.type = 'button'
  copyBtn.className = 'ptu-image-preview-address-btn'
  copyBtn.textContent = '复制'
  copyBtn.title = '复制地址'

  const closeBtn = document.createElement('button')
  closeBtn.type = 'button'
  closeBtn.className = 'ptu-image-preview-close'
  closeBtn.setAttribute('aria-label', '关闭')
  closeBtn.innerHTML = '&times;'

  const viewport = document.createElement('div')
  viewport.className = 'ptu-image-preview-viewport'

  const frame = document.createElement('div')
  frame.className = 'ptu-image-preview-frame bg-checker'

  const img = document.createElement('img')
  img.className = 'ptu-image-preview-img'
  img.src = url
  img.alt = title || '图片预览'
  img.draggable = false

  const footer = document.createElement('div')
  footer.className = 'ptu-image-preview-footer'

  const bgSwitch = document.createElement('span')
  bgSwitch.className = 'ptu-image-preview-bg-switch'

  const bgModes = [
    { key: 'checker', label: '棋盘格' },
    { key: 'white', label: '白底' },
    { key: 'dark', label: '深底' }
  ]
  let activeBgMode = 'checker'
  const bgButtons = []

  function setBackgroundMode(mode) {
    activeBgMode = mode
    frame.classList.remove('bg-checker', 'bg-white', 'bg-dark')
    frame.classList.add('bg-' + mode)
    bgButtons.forEach(function (btn) {
      btn.classList.toggle('is-active', btn.dataset.mode === mode)
    })
  }

  bgModes.forEach(function (item) {
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = 'ptu-image-preview-bg-btn' + (item.key === activeBgMode ? ' is-active' : '')
    btn.dataset.mode = item.key
    btn.textContent = item.label
    btn.addEventListener('click', function (event) {
      event.stopPropagation()
      setBackgroundMode(item.key)
    })
    bgButtons.push(btn)
    bgSwitch.appendChild(btn)
  })

  const footerTip = document.createElement('span')
  footerTip.textContent = '滚轮缩放 · 按住图片拖动 · 点击空白关闭 · ESC/空格 关闭'
  footer.appendChild(bgSwitch)
  footer.appendChild(footerTip)

  function updateCursor() {
    const cursor = isDragging ? 'grabbing' : ((scale > 1 || translateX !== 0 || translateY !== 0) ? 'grab' : 'zoom-in')
    img.style.cursor = cursor
    frame.style.cursor = cursor
  }

  function applyTransform() {
    frame.style.transform = 'translate(' + translateX + 'px, ' + translateY + 'px) scale(' + scale + ')'
    updateCursor()
  }

  function fitImageToViewport() {
    const paddingX = 24
    const paddingY = 12
    const maxW = Math.max(viewport.clientWidth - paddingX, 1)
    const maxH = Math.max(viewport.clientHeight - paddingY, 1)
    const naturalW = img.naturalWidth
    const naturalH = img.naturalHeight
    if (!naturalW || !naturalH) {
      return
    }
    const ratio = Math.min(maxW / naturalW, maxH / naturalH)
    frame.style.width = Math.floor(naturalW * ratio) + 'px'
    frame.style.height = Math.floor(naturalH * ratio) + 'px'
    scale = PREVIEW_DEFAULT_SCALE
    translateX = 0
    translateY = 0
    applyTransform()
  }

  img.addEventListener('load', function () {
    requestAnimationFrame(fitImageToViewport)
  })

  function navigateToAddress() {
    const nextUrl = (addressInput.value || '').trim()
    if (!nextUrl) {
      addressInput.value = img.src || url
      return
    }
    if (nextUrl === img.getAttribute('src')) {
      return
    }
    addressInput.value = nextUrl
    scale = PREVIEW_DEFAULT_SCALE
    translateX = 0
    translateY = 0
    applyTransform()
    img.src = nextUrl
  }

  function copyAddress() {
    const text = (addressInput.value || '').trim() || img.src || url
    function onCopied() {
      const prev = copyBtn.textContent
      copyBtn.textContent = '已复制'
      setTimeout(function () {
        if (!closed) {
          copyBtn.textContent = prev
        }
      }, 1200)
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(onCopied).catch(function () {
        fallbackCopy(text, onCopied)
      })
      return
    }
    fallbackCopy(text, onCopied)
  }

  function fallbackCopy(text, onCopied) {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.setAttribute('readonly', '')
    ta.style.cssText = 'position:fixed;left:-9999px;top:0;'
    document.body.appendChild(ta)
    ta.select()
    try {
      document.execCommand('copy')
      onCopied()
    } catch (e) {
      // ignore
    }
    document.body.removeChild(ta)
  }

  function onWheel(event) {
    event.preventDefault()
    const direction = event.deltaY < 0 ? 1 : -1
    scale = Math.min(
      PREVIEW_MAX_SCALE,
      Math.max(PREVIEW_MIN_SCALE, scale + direction * PREVIEW_ZOOM_STEP)
    )
    applyTransform()
  }

  function stopDragging() {
    if (!isDragging) {
      return
    }
    isDragging = false
    viewport.classList.remove('is-dragging')
    frame.classList.remove('is-dragging')
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('mouseup', onMouseUp)
    updateCursor()
  }

  function onViewportClick(event) {
    if (event.target === viewport) {
      closePreview()
    }
  }

  function onMouseDown(event) {
    if (event.button !== 0) {
      return
    }
    event.preventDefault()
    isDragging = true
    dragStartX = event.clientX
    dragStartY = event.clientY
    dragOriginX = translateX
    dragOriginY = translateY
    viewport.classList.add('is-dragging')
    frame.classList.add('is-dragging')
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
    updateCursor()
  }

  function onMouseMove(event) {
    if (!isDragging) {
      return
    }
    translateX = dragOriginX + (event.clientX - dragStartX)
    translateY = dragOriginY + (event.clientY - dragStartY)
    applyTransform()
  }

  function onMouseUp() {
    stopDragging()
  }

  function onKeydown(event) {
    const typingInAddress = document.activeElement === addressInput
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      closePreview()
      return
    }
    if (typingInAddress) {
      return
    }
    if (event.key === ' ' || event.code === 'Space') {
      event.preventDefault()
      event.stopPropagation()
      closePreview()
    }
  }

  function onAddressKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault()
      navigateToAddress()
      addressInput.blur()
    }
  }

  function closePreview() {
    if (closed) {
      return
    }
    closed = true
    stopDragging()
    viewport.removeEventListener('wheel', onWheel)
    viewport.removeEventListener('click', onViewportClick)
    frame.removeEventListener('mousedown', onMouseDown)
    img.removeEventListener('mousedown', onMouseDown)
    document.removeEventListener('keydown', onKeydown, true)
    overlay.removeEventListener('keydown', onKeydown)
    if (overlay.parentNode) {
      overlay.parentNode.removeChild(overlay)
    }
    document.body.style.overflow = previousOverflow
    if (activePreviewClose === closePreview) {
      activePreviewClose = null
    }
  }

  closeBtn.addEventListener('click', closePreview)
  copyBtn.addEventListener('click', function (event) {
    event.stopPropagation()
    copyAddress()
  })
  addressInput.addEventListener('keydown', onAddressKeydown)
  addressInput.addEventListener('focus', function () {
    requestAnimationFrame(function () {
      addressInput.select()
    })
  })
  addressInput.addEventListener('blur', function () {
    if (!(addressInput.value || '').trim()) {
      addressInput.value = img.getAttribute('src') || url
    }
  })
  overlay.addEventListener('click', closePreview)
  viewport.addEventListener('wheel', onWheel, { passive: false })
  viewport.addEventListener('click', onViewportClick)
  frame.addEventListener('mousedown', onMouseDown)
  img.addEventListener('mousedown', onMouseDown)
  overlay.addEventListener('keydown', onKeydown)
  document.addEventListener('keydown', onKeydown, true)

  addressBar.appendChild(addressInput)
  addressBar.appendChild(copyBtn)
  header.appendChild(addressBar)
  header.appendChild(closeBtn)
  frame.appendChild(img)
  viewport.appendChild(frame)
  panel.appendChild(header)
  panel.appendChild(viewport)
  panel.appendChild(footer)
  overlay.appendChild(panel)
  document.body.appendChild(overlay)
  document.body.style.overflow = 'hidden'
  overlay.focus()

  if (img.complete && img.naturalWidth) {
    requestAnimationFrame(fitImageToViewport)
  }
  activePreviewClose = closePreview
}

const DIALOG_DRAGOVER_CLASS = 'image-upload-dialog-dragover'
let dialogDragStyleInjected = false

function ensureDialogDragStyle() {
  if (dialogDragStyleInjected || typeof document === 'undefined') {
    return
  }
  dialogDragStyleInjected = true
  const style = document.createElement('style')
  style.textContent =
    '.el-dialog.' + DIALOG_DRAGOVER_CLASS + '{' +
    'outline:2px dashed #409eff;' +
    'outline-offset:-6px;' +
    '}'
  document.head.appendChild(style)
}

/**
 * 把文件拖放绑到上传组件所在的整窗 .el-dialog（标题栏、空白处都能收图）
 * @param {Element} componentEl 组件根节点
 * @param {{ onDrop: function(DragEvent): void }} options
 * @returns {{ unbind: function(): void }}
 */
export function bindDialogFileDrop(componentEl, options) {
  const empty = { unbind: function () {} }
  if (!componentEl || typeof componentEl.closest !== 'function') {
    return empty
  }
  const dialog = componentEl.closest('.el-dialog')
  if (!dialog) {
    return empty
  }
  ensureDialogDragStyle()

  let dragDepth = 0
  const onDrop = options && options.onDrop

  function clearDragover() {
    dragDepth = 0
    dialog.classList.remove(DIALOG_DRAGOVER_CLASS)
  }

  function onDragOver(event) {
    event.preventDefault()
  }

  function onDragEnter(event) {
    event.preventDefault()
    dragDepth += 1
    if (dragDepth === 1) {
      dialog.classList.add(DIALOG_DRAGOVER_CLASS)
    }
  }

  function onDragLeave(event) {
    event.preventDefault()
    dragDepth = Math.max(0, dragDepth - 1)
    if (dragDepth === 0) {
      dialog.classList.remove(DIALOG_DRAGOVER_CLASS)
    }
  }

  function onDialogDrop(event) {
    event.preventDefault()
    event.stopPropagation()
    clearDragover()
    if (typeof onDrop === 'function') {
      onDrop(event)
    }
  }

  dialog.addEventListener('dragover', onDragOver)
  dialog.addEventListener('dragenter', onDragEnter)
  dialog.addEventListener('dragleave', onDragLeave)
  dialog.addEventListener('drop', onDialogDrop)

  return {
    unbind: function () {
      dialog.removeEventListener('dragover', onDragOver)
      dialog.removeEventListener('dragenter', onDragEnter)
      dialog.removeEventListener('dragleave', onDragLeave)
      dialog.removeEventListener('drop', onDialogDrop)
      clearDragover()
    }
  }
}
